
import { usePlayerStore } from '~/stores/player';
import { takePlaybackQueue } from '~/components/mm/music/takeQueue';
import { useI18n } from 'vue-i18n';
import { musicService, type MusicTask, type GenMusic } from '~/services/musicService';
import { useToast } from '~/composables/useToast';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { useRouter, useRoute } from 'vue-router';
import { useAuthState } from '~/composables/useAuthState';
import { useShareIntent } from '~/components/mm/creator/incentives/useShareIntent';
import { generateFullSlug } from '~/utils/string';
import { trackEvent } from '~/utils/analytics';
import { usePaywall } from '~/composables/usePaywall';
import { pricingPath } from '~/utils/checkout-source.mjs';
import {
  buildMusicToMvDraft,
  consumeMusicToMvPendingRoute,
  getMusicToMvDraftAnalytics,
  isMusicEligibleForMvDraft,
  markMusicToMvPendingRoute,
  MUSIC_TO_MV_ROUTE,
  saveMusicToMvDraft,
  shouldTrackMusicToMvImpression,
  type MusicToMvDraftSource,
} from '~/utils/musicToMvDraft';


/** V3 adapter: MusicTask's request/guard/toast/analytics bodies are preserved. */
export interface TakeActionProps {
  task: MusicTask;
  playbackQueue?: GenMusic[];
  userName: string;
  mvUpsellTaskId: string;
  copyrightStates: Record<string, string>;
  copyrightEnabled: boolean;
  copyrightEntry: boolean;
  libraryPresentation: boolean;
}
export function useTakeActions(props: TakeActionProps, emit: (event: string, ...args: any[]) => void) {
const player = usePlayerStore();
const shareIntent = useShareIntent();
watch(() => props.task?.id, () => shareIntent.finish());
const { user, isAuthenticated, userAccount, handleSignIn, refreshUserAccount } = useAuthState();
const { openPaywall } = usePaywall();
const giftConfig = useRuntimeConfig();
const giftSong = ref<GenMusic | null>(null);
const giftShareEnabled = computed(() => {
  const owner = props.task?.user_id;
  return [true, 'true'].includes(giftConfig.public.giftShareEnabled)
    && isAuthenticated.value && (!owner || owner === userAccount.value?.id);
});
const openGiftShare = (music: GenMusic) => {
  if (!giftShareEnabled.value || !music?.id || pendingAction.value) return;
  showMoreOptions.value = false;
  giftSong.value = music;
};

const toast = useToast();
const { saveMusicData } = useMusicCreate();
const route = useRoute();
const router = useRouter();
const isMusicToMvUpsellEnabled = useMusicToMvUpsellFlag();

const { t } = useI18n();
const certificateLabel = (musicId: string) => t(
  props.copyrightStates[musicId] === 'issued' ? 'ux.viewCertificate'
    : props.copyrightStates[musicId] === 'processing' ? 'ux.certificateProgress'
      : props.copyrightEnabled && props.copyrightStates[musicId] === 'none' ? 'ux.getCertificate'
        : 'ux.copyrightCertificate'
);
const showCopyrightChoices = ref(false);
const copyrightFooterRef = ref<HTMLElement | null>(null);
const copyrightTriggerRef = ref<HTMLButtonElement | null>(null);
const copyrightChoicesRef = ref<HTMLElement | null>(null);
const taskCopyrightSongs = computed(() => currentTask.value?.status === 3
  ? (currentTask.value.music_data || []).filter(music => Boolean(
    (props.copyrightEntry || props.copyrightEnabled && props.copyrightStates[music.id]
      || ['issued', 'processing'].includes(props.copyrightStates[music.id]))
    && (music.audio_oss || music.audio_url)
  )) : []);
const taskCopyrightLabel = computed(() => {
  const songs = taskCopyrightSongs.value;
  if (songs.length && songs.every(music => props.copyrightStates[music.id] === 'issued')) return t('ux.viewCertificate');
  if (songs.some(music => ['issued', 'processing'].includes(props.copyrightStates[music.id]))) return t('ux.copyrightCertificate');
  return t('ux.getTaskCertificate');
});
const copyrightChoicesID = computed(() => `music-task-copyright-${currentTask.value.id}`);
const openTaskCopyright = () => {
  if (pendingAction.value || !taskCopyrightSongs.value.length) return;
  if (taskCopyrightSongs.value.length === 1) {
    emit('copyright', currentTask.value.id, taskCopyrightSongs.value[0].id);
    return;
  }
  showCopyrightChoices.value = !showCopyrightChoices.value;
  if (showCopyrightChoices.value) nextTick(() => copyrightChoicesRef.value?.querySelector<HTMLButtonElement>('button')?.focus());
};
const closeCopyrightChoices = () => {
  showCopyrightChoices.value = false;
  nextTick(() => copyrightTriggerRef.value?.focus({ preventScroll: true }));
};
const chooseCopyrightSong = (music: GenMusic) => {
  showCopyrightChoices.value = false;
  emit('copyright', currentTask.value.id, music.id);
};
const currentlyPlayingId = ref<string | null>(null);
const isCurrentlyPlaying = ref(false);

// 更多菜单状态
const showMoreOptions = ref(false);
const moreOptionsTriggerRefs = ref<Map<string, HTMLElement>>(new Map());
const moreOptionsMenuRef = ref<HTMLElement | null>(null);
const setMoreOptionsMenuElement = (element: Element | ComponentPublicInstance | null) => {
  moreOptionsMenuRef.value = element instanceof HTMLElement ? element : null;
};
const moreOptionsMenuStyle = ref<Record<string, string>>({
  left: '0px',
  top: '0px'
});
const activeMusic = ref<GenMusic | null>(null);
const activeIndex = ref<number>(-1);
const pendingAction = ref('');
const downloadError = ref({ id: '', message: '' });
const musicToMvImpressionObservers = new Set<IntersectionObserver>();

const closeMoreOptions = (restoreFocus = false) => {
  const trigger = activeMusic.value ? moreOptionsTriggerRefs.value.get(activeMusic.value.id) : null;
  showMoreOptions.value = false;
  activeMusic.value = null;
  activeIndex.value = -1;
  if (restoreFocus) {
    nextTick(() => {
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    });
  }
};

const focusMoreOptionsItem = (edge: 'first' | 'last' = 'first') => {
  const musicId = activeMusic.value?.id;
  nextTick(() => {
    if (!showMoreOptions.value || activeMusic.value?.id !== musicId) return;
    const items = moreOptionsMenuRef.value?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    const item = edge === 'last' ? items?.[items.length - 1] : items?.[0];
    item?.focus({ preventScroll: true });
  });
};

// 定位更多选项菜单
const positionMoreOptionsMenu = () => {
  if (!activeMusic.value || typeof window === 'undefined') return;

  const triggerElement = moreOptionsTriggerRefs.value.get(activeMusic.value.id);
  const menuElement = moreOptionsMenuRef.value;
  if (!triggerElement || !menuElement) return;

  const triggerRect = triggerElement.getBoundingClientRect();
  const menuWidth = menuElement.offsetWidth || 224;
  const menuHeight = menuElement.offsetHeight || 280;
  const gap = 10;
  const viewportPadding = 12;
  const maxLeft = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);

  let left = triggerRect.right - menuWidth;
  left = Math.min(Math.max(viewportPadding, left), maxLeft);

  let top = triggerRect.bottom + gap;
  if (top + menuHeight + viewportPadding > window.innerHeight) {
    top = triggerRect.top - menuHeight - gap;
  }
  const maxTop = Math.max(viewportPadding, window.innerHeight - menuHeight - viewportPadding);
  top = Math.min(Math.max(viewportPadding, top), maxTop);

  moreOptionsMenuStyle.value = {
    left: `${left}px`,
    top: `${top}px`
  };
};

const scheduleMoreOptionsPosition = () => {
  nextTick(() => {
    requestAnimationFrame(positionMoreOptionsMenu);
  });
};

const handleViewportChange = () => {
  if (showMoreOptions.value) {
    scheduleMoreOptionsPosition();
  }
};

// 设置更多菜单触发按钮引用
const setMoreOptionsTriggerRef = (el: Element | ComponentPublicInstance | null, id: string) => {
  if (!el) {
    moreOptionsTriggerRefs.value.delete(id);
    return;
  }

  if (el) {
    const htmlEl = el instanceof HTMLElement ? el : (el as ComponentPublicInstance).$el;
    if (htmlEl instanceof HTMLElement) {
      moreOptionsTriggerRefs.value.set(id, htmlEl);
    }
  }
};

const isEventInsideElement = (event: Event, element: HTMLElement | null) => {
  if (!(element instanceof HTMLElement)) return false;

  const target = event.target;
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

  return path.includes(element) || (target instanceof Node && element.contains(target));
};

const isEventInsideMoreOptions = (event: Event) => {
  const activeMenuId = activeMusic.value?.id;
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

  return path.some((item) => {
    if (!(item instanceof HTMLElement)) return false;

    return item.dataset.musicTaskMenu === 'true'
      || item.dataset.musicTaskMenuTrigger === activeMenuId;
  });
};

// 处理点击事件
const handleClickOutside = (event: Event) => {
  if (showCopyrightChoices.value && !isEventInsideElement(event, copyrightFooterRef.value)) {
    showCopyrightChoices.value = false;
  }
  // 如果没有显示菜单，直接返回
  if (!showMoreOptions.value) return;
  
  // 获取当前激活的菜单元素
  const activeMenuId = activeMusic.value?.id;
  if (!activeMenuId) return;
  
  const activeMenuEl = moreOptionsTriggerRefs.value.get(activeMenuId);
  const menuEl = moreOptionsMenuRef.value;
  if (!activeMenuEl && !menuEl) return;
  
  // 检查点击是否在更多选项按钮或菜单内部。Teleport 场景下 ref 可能短暂为空，
  // 因此同时使用 data 标记作为稳定命中条件，避免菜单项在 click 前被移除。
  const isOutside = !isEventInsideMoreOptions(event)
    && !isEventInsideElement(event, activeMenuEl || null)
    && !isEventInsideElement(event, menuEl);
  
  // 如果点击在外部，隐藏菜单
  if (isOutside) {
    closeMoreOptions();
  }
}

const handleMenuKeydown = (event: KeyboardEvent) => {
  if (!showMoreOptions.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeMoreOptions(true);
    return;
  }
  const menu = moreOptionsMenuRef.value;
  if (!(event.target instanceof Node) || !menu?.contains(event.target)) return;
  if (event.key === 'Tab') {
    const trigger = activeMusic.value ? moreOptionsTriggerRefs.value.get(activeMusic.value.id) : null;
    closeMoreOptions();
    // Start the browser's normal Tab movement at the original song, outside the Teleport.
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    return;
  }
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const items = Array.from(menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'));
  if (!items.length) return;
  const index = items.findIndex(item => item === document.activeElement);
  const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
    : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
  items[nextIndex]?.focus({ preventScroll: true });
};

// 组件挂载时添加点击事件监听
onMounted(() => {
  document.addEventListener('pointerdown', handleClickOutside, true);
  document.addEventListener('keydown', handleMenuKeydown);
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);
});

// 组件卸载时移除点击事件监听
onUnmounted(() => {
  document.removeEventListener('pointerdown', handleClickOutside, true);
  document.removeEventListener('keydown', handleMenuKeydown);
  window.removeEventListener('playing-state-changed', handlePlayingStateChanged);
  window.removeEventListener('resize', handleViewportChange);
  window.removeEventListener('scroll', handleViewportChange, true);
  musicToMvImpressionObservers.forEach((observer) => observer.disconnect());
  musicToMvImpressionObservers.clear();
  moreOptionsTriggerRefs.value.clear();
});

// 打开更多选项菜单
const openMoreOptions = (music: GenMusic, index: number, event: Event, edge: 'first' | 'last' = 'first') => {
  event.stopPropagation();
  if (pendingAction.value) return;
  
  // 如果点击的是同一个菜单，则切换显示状态
  if (activeMusic.value?.id === music.id && activeIndex.value === index) {
    showMoreOptions.value = !showMoreOptions.value;
    if (!showMoreOptions.value) {
      closeMoreOptions();
    } else {
      scheduleMoreOptionsPosition();
      focusMoreOptionsItem(edge);
    }
  } else {
    // 否则打开该菜单，关闭其他菜单
    showMoreOptions.value = true;
    activeMusic.value = music;
    activeIndex.value = index;
    
    scheduleMoreOptionsPosition();
    focusMoreOptionsItem(edge);
  }
}

const startPendingAction = (key: string) => {
  if (pendingAction.value) return false;
  pendingAction.value = key;
  return true;
};

const finishPendingAction = () => {
  pendingAction.value = '';
};

// 播放状态变化事件处理函数
const handlePlayingStateChanged = ((e: CustomEvent) => {
  const { id, isPlaying } = e.detail;
  currentlyPlayingId.value = id;
  isCurrentlyPlaying.value = isPlaying;
}) as EventListener;

// 监听任务状态
const localTask = ref<MusicTask | null>(null);

// 将props.task的值复制到本地变量
watch(() => props.task, (newTask) => {
  if (newTask) {
    localTask.value = JSON.parse(JSON.stringify(newTask));
  }
}, { immediate: true, deep: true });

const hasMounted = ref(false);
const musicTaskMonitor = useTaskMonitor<MusicTask>({
  load: async (id) => {
    const response = await musicService.getMusicTaskResult({ task_id: id });
    if (response.code !== 200) throw new Error(response.msg);
    return response.data;
  },
  pending: (task) => { localTask.value = task; emit('task-updated', task); },
  complete: (task) => { localTask.value = task; emit('task-updated', task); },
  failed: (task) => { localTask.value = task; emit('task-updated', task); },
  stopped: () => {},
  connectionMessage: () => t('ux.checkConnection'),
  timeoutMessage: () => t('ux.taskStillRunning'),
  intervalMs: 10_000,
  timeoutMs: 600_000,
});
const { error: musicPollingError, activeTaskId: pendingMusicTaskId } = musicTaskMonitor;
const monitorCurrentTask = () => {
  if (!hasMounted.value || !localTask.value) return;
  if (localTask.value.status < 3) {
    if (pendingMusicTaskId.value !== localTask.value.id) musicTaskMonitor.start(localTask.value.id);
  } else musicTaskMonitor.reset();
};
const checkMusicResultAgain = () => {
  if (pendingMusicTaskId.value) musicTaskMonitor.start(pendingMusicTaskId.value);
};
watch(() => [props.task?.id, props.task?.status], monitorCurrentTask);

// 使用计算属性处理任务数据，优先使用本地任务数据
const currentTask = computed(() => {
  const task = localTask.value || props.task;
  // Failed/pending API tasks can omit music_data (omitempty) or return null.
  // Keep existing song arrays intact, including their editing/playback identity.
  return Array.isArray(task.music_data) ? task : { ...task, music_data: [] };
});

// 根据任务状态判断是否在生成中
const isGenerating = computed(() => {
  if (!currentTask.value) return false;
  return currentTask.value.status < 3;
});

// 获取任务标题
const getTaskTitle = (task: MusicTask): string => {
  if (!task || !task.music_data || task.music_data.length === 0) {
    return t('create.status.defaultTitle');
  }

  // 使用第一首音乐的标题，如果没有则使用prompt
  const firstMusic = task.music_data[0];
  return firstMusic.title || firstMusic.metadata.prompt || '';
};

// 格式化时间
const formatDuration = (seconds: number | undefined) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Each submitted task keeps a bounded monitor; leaving the card disposes it.
onMounted(() => {
  window.addEventListener('playing-state-changed', handlePlayingStateChanged);
  hasMounted.value = true;
  monitorCurrentTask();
});

// 检查是否正在播放
const isPlaying = (music: GenMusic) => {
  return currentlyPlayingId.value === music.id && isCurrentlyPlaying.value;
};

// 切换播放状态
const togglePlayState = (music: GenMusic) => {
  // 验证音频URL
  if (!music || !music.audio_url) {
    console.error('播放失败: 音频URL不存在', music);
    const event = new CustomEvent('show-toast', {
      detail: { type: 'error', message: t('music.playFailed') }
    });
    window.dispatchEvent(event);
    return;
  }
  
  // 如果音乐正在生成中，显示提示并返回
  if(isGenerating.value && (!music.audio_url || !music.metadata?.duration)) {
    const event = new CustomEvent('show-toast', {
      detail: { type: 'error', message: t('create.waitMessage') }
    });
    window.dispatchEvent(event);
    return;
  }
  
  try {
    // 直接触发全局播放事件，而不仅仅是emit
    if (currentlyPlayingId.value === music.id) {
      // 如果是当前播放的音乐，则切换播放状态
      const newPlayingState = !isCurrentlyPlaying.value;
      
      // 触发全局播放状态切换事件
      const event = new CustomEvent('toggle-play-state', {
        detail: { isPlaying: newPlayingState }
      });
      window.dispatchEvent(event);
      
      // 在发送事件后更新本地状态
      isCurrentlyPlaying.value = newPlayingState;
    } else {
      // 不是当前播放的音乐，则播放这首音乐
      // 创建一个新对象，避免引用问题
      music.audio_url = music.audio_oss || music.audio_url;
      // Keep the legacy player surface; only add the displayed list queue.
      player.play({ ...music }, {}, { queue: props.playbackQueue ?? takePlaybackQueue([currentTask.value]) });
      
      // 更新本地状态 - 在发送事件后更新本地状态
      currentlyPlayingId.value = music.id;
      isCurrentlyPlaying.value = true;
    }
    
    // 依然保留emit，以便父组件可以做额外处理
    emit('play', music);
  } catch (error) {
    console.error('播放事件触发失败:', error);
    const event = new CustomEvent('show-toast', {
      detail: { type: 'error', message: t('music.playFailed') }
    });
    window.dispatchEvent(event);
  }
};

// 删除任务函数
const deleteTask = async () => {
  if (!localTask.value || !localTask.value.id) return;
  if (!startPendingAction('delete-task')) return;
  
  try {
    const res = await musicService.deleteRecord({
      category: 1, // 1表示删除任务
      ids: [localTask.value.id]
    });
    
    if (res.code === 200) {
      // 删除成功
      const event = new CustomEvent('show-toast', {
        detail: { type: 'success', message: t('music.deleteSuccess') }
      });
      window.dispatchEvent(event);
      
      // 更新本地任务
      if (localTask.value) {
        localTask.value.status = 4; // 设置为失败状态
        emit('task-updated', localTask.value);
      }
    } else {
      // 删除失败
      const event = new CustomEvent('show-toast', {
        detail: { type: 'error', message: t('music.deleteFailed') }
      });
      window.dispatchEvent(event);
    }
  } catch (error) {
    console.error('删除任务失败:', error);
    const event = new CustomEvent('show-toast', {
      detail: { type: 'error', message: t('music.deleteFailed') }
    });
    window.dispatchEvent(event);
  } finally {
    finishPendingAction();
    showMoreOptions.value = false;
  }
};

// 删除单个音乐
const deleteSingleMusic = async (music: GenMusic) => {
  if (!music || !music.id) return;
  if (!startPendingAction(`delete-music-${music.id}`)) return;
  
  try {
    // 检查是否是最后一首歌曲
    const isLastSong = localTask.value?.music_data && localTask.value.music_data.length === 1;
    
    const res = await musicService.deleteRecord({
      category: isLastSong ? 1 : 2, // 如果是最后一首歌曲，则删除整个任务(category=1)，否则只删除单首歌曲(category=2)
      ids: isLastSong ? [localTask.value?.id || ''] : [music.id]
    });
    
    if (res.code === 200) {
      // 删除成功
      toast.success(t('music.deleteSuccess'));
      
      if (isLastSong) {
        // 如果是最后一首歌曲，设置任务状态为失败(4)
        if (localTask.value) {
          localTask.value.status = 4;
          emit('task-updated', localTask.value);
        }
      } else {
        // 否则从列表中移除该音乐
        if (localTask.value && localTask.value.music_data) {
          localTask.value.music_data = localTask.value.music_data.filter(item => item.id !== music.id);
          
          // 检查删除后是否没有歌曲了
          if (localTask.value.music_data.length === 0) {
            // 如果没有歌曲了，将任务状态设置为失败(4)
            localTask.value.status = 4;
          }
          
          emit('task-updated', localTask.value);
        }
      }
    } else {
      // 删除失败
      toast.error(t('music.deleteFailed'));
    }
  } catch (error) {
    console.error('删除音乐失败:', error);
    toast.error(t('music.deleteFailed'));
  } finally {
    finishPendingAction();
    showMoreOptions.value = false;
  }
};

// 创建同款音乐
const createSimilarMusic = (music: GenMusic) => {
  if (!music) return;
  try {
    // 从音乐元数据中提取歌词信息或使用描述作为歌词
    const lyricsContent = music.metadata?.prompt || '';
    const musicTitle = music.title || '';
    // 确保标签是字符串格式
    const tagsString = typeof music.metadata?.tags === 'string' ? 
                      music.metadata.tags : 
                      (music.metadata?.tags ? String(music.metadata.tags) : '');
    
    // 准备做同款数据
    const similarData = {
      prompt: music.metadata?.prompt || '',
      tags: tagsString,
      instrumental: currentTask.value?.instrumental || false,
      lyrics: lyricsContent,
      title: musicTitle,
      mode: 'advanced'
    };
    
    // 使用更新后的saveMusicData方法，并传递当前路由路径
    saveMusicData(similarData, route.path);
    
    // 显示成功提示
    if (route.path === '/create') {
      // toast.success(t('music.similarMusicApplied'));
    } else {
      router.push('/create');
      // toast.success(t('music.similarMusicRedirect'));
    }
  } catch (error) {
    toast.error(t('common.operation_failed'));
  }
  
  // 关闭菜单
  showMoreOptions.value = false;
};

// 导航到音乐详情页
const canLaunchMusicToMv = (music: GenMusic) => {
  return Boolean(currentTask.value?.id && isMusicEligibleForMvDraft(music));
};

const getMusicToMvSurfaceParams = (music: GenMusic, source: MusicToMvDraftSource) => ({
  surface: source === 'generation_success' ? 'generation_success' : 'song_card',
  task_id: currentTask.value?.id || '',
  music_id: music.id,
  duration: Number(music.metadata?.duration || 0),
  has_lyrics_or_prompt: Boolean(
    music.title
    || music.metadata?.prompt
    || music.metadata?.tags
    || music.metadata?.gpt_description_prompt
  ),
});

const trackMusicToMvImpression = (music: GenMusic, source: MusicToMvDraftSource) => {
  if (!canLaunchMusicToMv(music)) return;
  const params = getMusicToMvSurfaceParams(music, source);
  if (!shouldTrackMusicToMvImpression({
    surface: params.surface,
    taskId: params.task_id,
    musicId: params.music_id,
  })) {
    return;
  }
  trackEvent('music_to_mv_cta_impression', params);
};

const observeMusicToMvImpression = (
  el: Element | ComponentPublicInstance | null | undefined,
  music: GenMusic,
  source: MusicToMvDraftSource
) => {
  if (!el || typeof window === 'undefined' || !canLaunchMusicToMv(music)) return;
  const element = el instanceof Element ? el : el.$el;
  if (!(element instanceof Element)) return;

  if (!('IntersectionObserver' in window)) {
    trackMusicToMvImpression(music, source);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
      trackMusicToMvImpression(music, source);
      observer.disconnect();
      musicToMvImpressionObservers.delete(observer);
    }
  }, { threshold: 0.1 });

  musicToMvImpressionObservers.add(observer);
  observer.observe(element);
};

const launchMusicToMv = async (music: GenMusic, source: MusicToMvDraftSource) => {
  if (!canLaunchMusicToMv(music)) {
    toast.error(t('music.makeVideoUnavailable'));
    return;
  }
  if (!startPendingAction(`music-to-mv-${music.id}`)) return;

  try {
    const draft = buildMusicToMvDraft({
      source,
      taskId: currentTask.value?.id || '',
      music,
    });

    if (!draft || !saveMusicToMvDraft(draft)) {
      toast.error(t('music.makeVideoUnavailable'));
      return;
    }

    const clickParams = {
      ...getMusicToMvSurfaceParams(music, source),
      cta_label: source === 'generation_success' ? 'make_10s_video' : 'make_video',
    };

    trackEvent('music_to_mv_cta_click', clickParams);
    trackEvent('music_to_mv_draft_created', getMusicToMvDraftAnalytics(draft));

    emit('mv-upsell-dismissed', currentTask.value?.id || '');
    showMoreOptions.value = false;

    if (!isAuthenticated.value) {
      markMusicToMvPendingRoute();
      trackEvent('music_to_mv_login_required', clickParams);
      toast.info(t('music.makeVideoLoginRequired'));
      await handleSignIn();
      return;
    }

    await router.push(MUSIC_TO_MV_ROUTE);
  } finally {
    finishPendingAction();
  }
};

const firstPlayableMusic = computed(() => currentTask.value?.music_data?.find(canLaunchMusicToMv) || null);

const shouldShowMvUpsell = computed(() => {
  return Boolean(
    isMusicToMvUpsellEnabled.value
    && props.mvUpsellTaskId
    && props.mvUpsellTaskId === currentTask.value?.id
    && currentTask.value?.status === 3
    && firstPlayableMusic.value
  );
});

watch(
  shouldShowMvUpsell,
  (visible) => {
    if (visible && firstPlayableMusic.value) {
      trackMusicToMvImpression(firstPlayableMusic.value, 'generation_success');
    }
  },
  { immediate: true }
);

watch(
  isAuthenticated,
  (signedIn) => {
    if (signedIn && consumeMusicToMvPendingRoute()) {
      router.push(MUSIC_TO_MV_ROUTE);
    }
  },
  { immediate: true }
);

const navigateToMusicDetail = (taskId: string, music: GenMusic) => {
  // 简单地导航到详情页，只传递ID
  router.push(`/music/song/${music.id}?taskId=${taskId}`);
};
// 下载音乐
const handleDownloadMusic = async(music: any) => {
  if (currentTask.value.status !== 3 || !(music?.audio_oss || music?.audio_url)) return;
  downloadError.value = { id: '', message: '' };
  if (!music?.id) {
    toast.error(t('music.downloadFailed'));
    return;
  }

  if (!user.value) {
    toast.error(t('user.loginToDownloadMusic'));
    await handleSignIn();
    return;
  }

  if (!userAccount.value) {
    downloadError.value = { id: music.id, message: t('common.load_failed') };
    return;
  }

  if(userAccount.value && userAccount.value.vip_level > 0) {
    const audioUrl = music.audio_oss || music.audio_url;
    if (!audioUrl) {
      toast.error(t('music.downloadFailed'));
      return;
    }

    if (!startPendingAction(`download-${music.id}`)) return;
    showMoreOptions.value = false;
    toast.info(t('music.downloadStarted'));

    try {
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${music.title || 'music'}.mp3`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('music.downloadSuccess'));
    } catch (error) {
      console.error('下载MP3文件时出错:', error);
      const fallbackWindow = window.open(audioUrl, '_blank');
      if (fallbackWindow) {
        fallbackWindow.opener = null;
        toast.info(t('music.downloadStarted'));
      } else {
        toast.error(t('music.downloadFailed'));
      }
    } finally {
      finishPendingAction();
    }
  } else {
    trackEvent('download_blocked_membership', { surface: 'music_task', task_id: currentTask.value?.id || '', music_id: music.id });
    openPaywall({ trigger: 'download', surface: 'music_task' });
  }
};
const retryDownloadMusic = async (music: GenMusic) => {
  await refreshUserAccount();
  await handleDownloadMusic(music);
};
// 修改歌曲名称
const showTitleModal = ref(false);
const editingMusic = ref<any | null>(null);
const newTitle = ref('');
const titleError = ref('');
const titleDialog = ref<HTMLElement | null>(null);
const titleInput = ref<HTMLInputElement | null>(null);
const titleDialogID = computed(() => `music-task-title-${currentTask.value.id}`);
let titlePreviousFocus: HTMLElement | null = null;
let titlePreviousOverflow: string | undefined;
const restoreTitleDialog = () => {
  if (titlePreviousOverflow !== undefined) document.body.style.overflow = titlePreviousOverflow;
  titlePreviousOverflow = undefined;
  if (titlePreviousFocus?.isConnected) titlePreviousFocus.focus();
  titlePreviousFocus = null;
};
const closeTitleDialog = () => {
  if (!pendingAction.value) showTitleModal.value = false;
};
const trapTitleFocus = (event: KeyboardEvent) => {
  const items = Array.from(titleDialog.value?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') || []).filter(item => item.getClientRects().length);
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
};
// V3 MmDialog owns focus trapping, scroll locking, and focus return.


const openTitleEditModal = (currentTask: any, music: GenMusic, event: Event) => {
  event.stopPropagation();
  if (pendingAction.value) return;
  editingMusic.value = currentTask;
  newTitle.value = music.title || '';
  titleError.value = '';
  titlePreviousFocus = moreOptionsTriggerRefs.value.get(music.id) || null;
  showTitleModal.value = true;
  showMoreOptions.value = false;
}

const updateMusicTitle = async () => {
  if (!editingMusic.value || !editingMusic.value.id || !newTitle.value.trim()) return;
  if (!startPendingAction('update-title')) return;
  titleError.value = '';
  
  try {
    const params = {
      task_id: editingMusic.value.id,
      title: newTitle.value.trim()
    }
    
    const res = await musicService.updateMusicTitle(params);
    
    if(res.code === 200) {
      toast.success(t('music.updateTitleSuccess'));
      // 更新本地音乐列表中的标题
      localTask.value?.music_data?.forEach(music => {
        music.title = newTitle.value.trim();
      });
      showTitleModal.value = false;
      editingMusic.value = null;
      newTitle.value = '';
    } else {
      titleError.value = t('music.updateTitleFailed');
    }
  } catch (error) {
    titleError.value = t('music.updateTitleFailed');
  } finally {
    finishPendingAction();
  }
}

const handleUpdateTitleClick = () => {
  if (pendingAction.value) return;
  if (!newTitle.value.trim()) {
    toast.error(t('music.enterNewTitle'));
    return;
  }
  updateMusicTitle();
}
// 分享至facebook/twitter
const handleShare = async (music: GenMusic, type: string) => {
  if (!music?.id || !startPendingAction(`share-${music.id}`)) return;
  const shareContext = shareIntent.version();
  const shareWindow = window.open('about:blank', '_blank');
  if (shareWindow) {
    shareWindow.opener = null;
    shareWindow.document.write('<!doctype html><title>MusicMint</title><body></body>');
    shareWindow.document.body.textContent = t('mm.take.preparingShare');
  }

  try {
    const res = await shareIntent.generate({ id: music.id });
    if (!shareIntent.isCurrent(shareContext)) { shareWindow?.close(); return; }
    if(res.code === 200) {
      // Generate SEO-friendly slug URL
      const slug = generateFullSlug(res.data.token, music.title);
      let url = `https://musicmint.ai/share/${slug}`;

      if(type === 'facebook') {
        let facebookUrl = `https://www.facebook.com/sharer.php?u=${url}`;
        if (shareWindow) {
          shareWindow.location.href = facebookUrl;
        } else {
          window.open(facebookUrl, '_blank');
        }
      } else if(type === 'twitter') {
        let twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`;
        if (shareWindow) {
          shareWindow.location.href = twitterUrl;
        } else {
          window.open(twitterUrl, '_blank');
        }
      } else if (shareWindow) {
        shareWindow.close();
      }
    } else {
      shareWindow?.close();
      toast.error(t('common.operation_failed'));
    }
    shareIntent.finish();
  } catch (error) {
    shareWindow?.close();
    if (shareIntent.isCurrent(shareContext)) toast.error(t(shareIntent.errorKey(error)));
  } finally {
    finishPendingAction();
    showMoreOptions.value = false;
  }
}

return { giftSong, giftShareEnabled, openGiftShare, toast, route, router, isMusicToMvUpsellEnabled, certificateLabel, showCopyrightChoices, copyrightFooterRef, copyrightTriggerRef, copyrightChoicesRef, taskCopyrightSongs, taskCopyrightLabel, copyrightChoicesID, openTaskCopyright, closeCopyrightChoices, chooseCopyrightSong, currentlyPlayingId, isCurrentlyPlaying, showMoreOptions, moreOptionsTriggerRefs, moreOptionsMenuRef, setMoreOptionsMenuElement, moreOptionsMenuStyle, activeMusic, activeIndex, pendingAction, downloadError, musicToMvImpressionObservers, closeMoreOptions, focusMoreOptionsItem, positionMoreOptionsMenu, scheduleMoreOptionsPosition, handleViewportChange, setMoreOptionsTriggerRef, isEventInsideElement, isEventInsideMoreOptions, handleClickOutside, handleMenuKeydown, openMoreOptions, startPendingAction, finishPendingAction, handlePlayingStateChanged, localTask, hasMounted, musicTaskMonitor, monitorCurrentTask, checkMusicResultAgain, currentTask, isGenerating, getTaskTitle, formatDuration, isPlaying, togglePlayState, deleteTask, deleteSingleMusic, createSimilarMusic, canLaunchMusicToMv, getMusicToMvSurfaceParams, trackMusicToMvImpression, observeMusicToMvImpression, launchMusicToMv, firstPlayableMusic, shouldShowMvUpsell, navigateToMusicDetail, handleDownloadMusic, retryDownloadMusic, showTitleModal, editingMusic, newTitle, titleError, titleDialog, titleInput, titleDialogID, restoreTitleDialog, closeTitleDialog, trapTitleFocus, openTitleEditModal, updateMusicTitle, handleUpdateTitleClick, handleShare, musicPollingError, pendingMusicTaskId };
}
